<div class="poc-section-title">
    <h3>Übersicht</h3>
</div>
<p class="text-color-secondary text-sm mt-0 mb-4">
    Prüfen Sie Ihre Konfiguration. Über "Bearbeiten" können Sie einzelne Schritte anpassen.
</p>

<!-- ════════════════════════════════════════════════════════════
     BASICS
     ════════════════════════════════════════════════════════════ -->
<div class="poc-overview-section">
    <div class="poc-overview-section__header">
        <span class="poc-overview-section__title">
            <i class="fa-solid fa-clipboard-list text-primary mr-2"></i>Grundlagen
        </span>
        <p-button label="Bearbeiten" icon="fa-solid fa-pen" severity="secondary" [text]="true" size="small"
                  (onClick)="onEdit(1)">
        </p-button>
    </div>
    <div class="grid text-sm" style="row-gap: 0.5rem">
        <div class="col-4 font-medium text-color-secondary">Name</div>
        <div class="col-8 font-semibold">{{ state.basics.name || '—' }}</div>
        <div class="col-4 font-medium text-color-secondary">Beschreibung</div>
        <div class="col-8">{{ state.basics.description || '—' }}</div>
        <div class="col-4 font-medium text-color-secondary">Gruppe</div>
        <div class="col-8">{{ state.basics.groupName || '—' }}</div>
        <div class="col-4 font-medium text-color-secondary">Typ</div>
        <div class="col-8">{{ typeLabel }}</div>
        <div class="col-4 font-medium text-color-secondary">Aktiv</div>
        <div class="col-8">
            <p-tag [value]="state.basics.active ? 'Ja' : 'Nein'"
                   [severity]="state.basics.active ? 'success' : 'warn'" [rounded]="true">
            </p-tag>
        </div>
        <div class="col-4 font-medium text-color-secondary">Inhalt speichern</div>
        <div class="col-8">
            <p-tag [value]="state.basics.storeContent ? 'Ja' : 'Nein'"
                   [severity]="state.basics.storeContent ? 'success' : 'secondary'" [rounded]="true">
            </p-tag>
        </div>
    </div>
</div>

<!-- ════════════════════════════════════════════════════════════
     CHANNELS
     ════════════════════════════════════════════════════════════ -->
<div class="poc-overview-section">
    <div class="poc-overview-section__header">
        <span class="poc-overview-section__title">
            <i class="fa-solid fa-paper-plane text-primary mr-2"></i>
            Kanäle
            <span class="poc-section-badge ml-2">{{ selectedChannels.length }}</span>
        </span>
        <p-button label="Bearbeiten" icon="fa-solid fa-pen" severity="secondary" [text]="true" size="small"
                  (onClick)="onEdit(2)">
        </p-button>
    </div>

    @for (ch of selectedChannels; track ch.channelTypeId) {
        <div class="flex align-items-start gap-3 mb-3 p-2 border-round surface-ground">
            <i [class]="ch.channelTypeIcon + ' mt-1'" style="color: var(--primary-color); width: 20px; text-align: center"></i>
            <div class="text-sm">
                <span class="font-semibold">{{ ch.channelTypeName }}</span>
                <div class="text-color-secondary mt-1">
                    Vorlage: {{ ch.createNewTemplate ? 'Neue Standardvorlage' : (ch.templateName || 'Nicht ausgewählt') }}
                </div>
                @if (ch.subjectText) {
                    <div class="text-color-secondary">Betreff: "{{ ch.subjectText }}"</div>
                }
                @if (ch.hasFlexibleSubject && ch.subjectTemplateDtoName) {
                    <div class="text-color-secondary">Betreff-Vorlage: {{ ch.subjectTemplateDtoName }}</div>
                }
            </div>
        </div>
    }

    @if (selectedChannels.length === 0) {
        <span class="text-color-secondary text-sm">Keine Kanäle konfiguriert.</span>
    }
</div>

<!-- ════════════════════════════════════════════════════════════
     FIELDS
     ════════════════════════════════════════════════════════════ -->
<div class="poc-overview-section">
    <div class="poc-overview-section__header">
        <span class="poc-overview-section__title">
            <i class="fa-solid fa-layer-group text-primary mr-2"></i>
            Felder
            <span class="poc-section-badge ml-2">{{ state.fieldDefinitions.length }}</span>
        </span>
        <p-button label="Bearbeiten" icon="fa-solid fa-pen" severity="secondary" [text]="true" size="small"
                  (onClick)="onEdit(3)">
        </p-button>
    </div>

    @for (field of state.fieldDefinitions; track field.id) {
        <div class="flex align-items-center gap-2 mb-2 text-sm">
            <span class="font-semibold" style="min-width: 100px">{{ field.name }}</span>
            <p-tag [value]="field.fieldTypeDtoName || '—'" severity="info" [rounded]="true"></p-tag>
            @if (field.mandatory) {
                <p-tag value="Pflicht" severity="warn" [rounded]="true"></p-tag>
            }
            @if (field.storeRecipient) {
                <p-tag value="Empfänger" severity="success" [rounded]="true"></p-tag>
            }
        </div>
    }

    @if (state.fieldDefinitions.length === 0) {
        <span class="text-color-secondary text-sm">Keine Felder definiert (optional).</span>
    }
</div>

<!-- ════════════════════════════════════════════════════════════
     CONFIG CHECK
     ════════════════════════════════════════════════════════════ -->
<div class="poc-overview-section">
    <div class="poc-overview-section__header">
        <span class="poc-overview-section__title">
            <i class="fa-solid fa-shield-check text-primary mr-2"></i>
            Konfigurationsprüfung
        </span>
    </div>

    @for (check of state.checkItems; track check.message) {
        <div class="poc-check-item">
            <i [class]="getSeverityIcon(check.severity)"></i>
            <span [ngClass]="{
                'text-green-700': check.severity === PocCheckSeverity.pass,
                'text-yellow-700': check.severity === PocCheckSeverity.warning,
                'text-red-700': check.severity === PocCheckSeverity.error,
                'text-color-secondary': check.severity === PocCheckSeverity.info
            }">
                {{ check.message }}
            </span>
        </div>
    }
</div>

<!-- ════════════════════════════════════════════════════════════
     NAVIGATION
     ════════════════════════════════════════════════════════════ -->
<div class="poc-nav-footer">
    <p-button label="Zurück" icon="fa-solid fa-arrow-left" severity="secondary" [outlined]="true"
              (onClick)="onBack()">
    </p-button>
    <p-button label="Erstellen & Aktivieren" icon="fa-solid fa-check" severity="success"
              [disabled]="!state.canActivate" (onClick)="onActivate()">
    </p-button>
</div>
